<template lang="pug">
div.verse(:class="classes")
  div.verse__heading(
    v-if="showHeading && (subHeading?.length || heading?.length)"
  )
    div.verse__heading-ref(
      v-if="subHeading?.length"
      v-html="subHeading"
    )
    HH.text-h3.verse__heading-title(v-if="heading?.length && searchMatches")
      template(
        v-for="(segment, index) in searchHeadingSegments"
        :key="index"
      )
        mark(v-if="segment.match") {{ segment.text }}
        span(v-else) {{ segment.text }}
    HH.text-h3.verse__heading-title(
      v-else-if="heading?.length"
      v-html="heading"
    )
  p.verse__content.text-body-1(:class="{ 'verse__content--dense': dense }")
    span.verse__number(:class="{ 'verse__number--outer': refNumberOuter }") {{ verse.label }}
    | {{ " " }}
    span.verse__text.verse__text--search(
      v-if="searchMatches"
      :class="`verse__text--${verse.tid}`"
    )
      template(
        v-for="(segment, index) in searchSegments"
        :key="index"
      )
        mark(
          v-if="segment.match"
          :class="{ 'font-italic': segment.emphasis }"
        ) {{ segment.text }}
        span(
          v-else
          :class="{ 'font-italic': segment.emphasis }"
        ) {{ segment.text }}
    span.verse__text(
      v-else
      :class="`verse__text--${verse.tid}`"
      v-html="verseText"
    )
    VLazy(
      v-if="showStudyButton"
      tag="span"
    )
      BibleVerseCommentList.verse__note(
        v-if="studyButtonListComments"
        :range="verse"
      )
        template(#activator="{ activatorProps, countLocale }")
          button(
            :class="{ verse__noteBtn: true, 'verse__noteBtn--soft': countLocale === 0 }"
            type="button"
            v-bind="activatorProps['props']"
            data-testid="commentListVerseButton"
            :title="i18n.t('BibleVerse.listComments')"
            :aria-label="i18n.t('BibleVerse.listComments')"
          )
            VIcon(size="x-small") {{ commentaryOutline }}
      BibleVerseStudyButton.verse__note(
        v-else
        :range="verse"
      )
        template(#activator="{ activatorProps, countLocale }")
          button(
            :class="{ verse__noteBtn: true, 'verse__noteBtn--soft': countLocale === 0 }"
            type="button"
            v-bind="activatorProps['props']"
            data-testid="studyThisVerseButton"
            :title="i18n.t('BibleVerse.studyThisVerse')"
            :aria-label="i18n.t('BibleVerse.studyThisVerse')"
          )
            VIcon(size="x-small") {{ mdiFormatListBulletedSquare }}
</template>

<script setup lang="ts">
/**
 * A component that displays a single Bible verse.
 */
import type { SearchResultV2 } from "@rsc/scripture-model";
import { searchTextSegments } from "./BibleSearchVerseResult.util";
import type { Verse } from "~/units/scripture";
import {
  formatVerseText,
  isFirstVerseInChapter,
  makeVerseHeading,
} from "~/units/scripture";
import "~/assets/fonts/SBLHebrew.css";
import { computed } from "vue";
import { commentaryOutline } from "~/icons";
import HH from "~/headings/components/HH.vue";
import BibleVerseCommentList from "./BibleVerseCommentList.vue";
import { mdiFormatListBulletedSquare } from "@mdi/js";

const i18n = useI18n();

interface Props {
  /**
   * The Verse object to display.
   */
  verse: Verse;

  /**
   * Whether this verse is the first one in a list of verses.
   */
  first?: boolean;

  /**
   * Whether to show the book and chapter as a verse heading.
   *
   * Available options:
   * - 'auto' (default): When the verse is first in the chapter, or the verse is the first one in the list of verses.
   * - 'never': Never show the chapter and verse as a heading.
   * - 'always': Always show the chapter and verse as a heading.
   */
  showBookAndChapter?: "auto" | "never" | "always";

  /**
   * Whether to show the translation in the heading.
   * Ignored if no heading is shown.
   */
  showTranslation?: boolean;

  /**
   * Whether to show the heading when available.
   */
  showHeading?: boolean;

  /**
   * The words to highlight in this verse.
   */
  selectedWordOrdinals?: number[];
  searchMatches?: SearchResultV2["matches"];

  /**
   * Whether to apply column styling (max-width and auto margins).
   */
  columnStyle?: boolean;

  /**
   * Place reference number on the left, outside the column.
   */
  refNumberOuter?: boolean;

  /**
   * Set a "dense" line-height of text content for some context
   */
  dense?: boolean;

  /**
   * Should display a study button with corresponding comments
   */
  showStudyButton?: boolean;

  /**
   * When showStudyButton is activated.
   * If flalse (default), will open a menu with list of link for available comment types for this verse
   * If true, will open a bottom sheet with list of comments
   */
  studyButtonListComments?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  showBookAndChapter: "auto",
  selectedWordOrdinals: () => [],
  showStudyButton: false,
  studyButtonListComments: false,
});

/**
 * The heading to display if necessary.
 */
const heading = computed<string>(() =>
  makeVerseHeading(props.verse, true, false, props.showTranslation),
);

const subHeading = computed<string>(() => {
  const showBookAndChapter =
    props.showBookAndChapter === "auto"
      ? isFirstVerseInChapter(props.verse) || props.first
      : props.showBookAndChapter === "always";

  if (showBookAndChapter) {
    return makeVerseHeading(props.verse, false, true, props.showTranslation);
  } else {
    return "";
  }
});

/**
 * The text to display in the verse
 */
const verseText = computed<string>(() =>
  formatVerseText(props.verse, props.selectedWordOrdinals),
);

const searchHeadingSegments = computed(() =>
  searchTextSegments(
    { verse: props.verse, matches: props.searchMatches ?? [] },
    { onlyHeadings: true },
  ),
);
const searchSegments = computed(() =>
  searchTextSegments(
    { verse: props.verse, matches: props.searchMatches ?? [] },
    { omitHeadings: true },
  ),
);

const classes = computed(() => ({
  [`verse--${props.verse.tid}`]: true,
  "verse--refNumberOuter": props.refNumberOuter,
  "verse--column": props.columnStyle,
}));
</script>

<style lang="scss">
@use "../assets/styles/abstract/fonts" as fo;

.partial-verse::before {
  content: attr(data-label);
  font-size: 0.8em;
  padding-right: 0.8ch;
  font-weight: bold;
  opacity: 0.8;
  font-family: fo.$body;
}
</style>

<style scoped lang="scss">
@use "../assets/styles/abstract/fonts" as fo;
@use "../assets/styles/abstract/functions" as f;

.verse__text--search {
  white-space: pre-wrap;
}
.verse__text--search mark,
.verse__heading-title mark {
  background: rgb(var(--v-theme-secondary-c100));
  color: rgb(var(--v-theme-primary-c900));
}

.verse__heading {
  margin-top: f.rem(24);
  margin-bottom: f.rem(12);
}

.verse__heading-ref {
  position: relative;
  font-family: fo.$body;
  font-weight: 700;
  font-size: f.rem(14px);
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-bottom: f.rem(8);
  color: rgb(var(--v-theme-primary-c700));

  &::before {
    content: "";
    position: absolute;
    bottom: -#{f.rem(2)};
    width: f.rem(24px);
    height: f.rem(2px);
    left: -#{f.rem(4)};
    background-color: rgb(var(--v-theme-primary-c100));
  }

  .v-theme--dark & {
    color: rgb(var(--v-theme-primary-c100));
    &::before {
      background-color: rgb(var(--v-theme-primary-c700));
    }
  }
}

.verse__heading-title {
  letter-spacing: 0.05em !important;
  line-height: 1.4em;
}

.verse__number {
  font-size: 0.8em;
  font-weight: bold;
  opacity: 0.8;
  font-family: fo.$body;
  display: inline-block;
}

.verse__number--outer {
  position: absolute;
  right: calc(100% + #{f.rem(4)});
  top: 0;
}

.verse {
  &--refNumberOuter {
    padding-left: f.rem(8);
  }

  &--column {
    max-width: f.rem(400);
    margin-left: auto;
    margin-right: auto;
    padding-left: f.rem(32);
    padding-right: f.rem(12);
  }
}

.verse__note {
  margin-left: f.rem(4);
  display: inline-flex;
  align-self: center;
  line-height: 1;
  vertical-align: baseline;
}

.verse__noteBtn {
  margin: 0;
  display: inline-flex;
  height: f.rem(20);
  line-height: 1;
  padding: f.rem(1) f.rem(4);
  border-radius: f.rem(4);
  background-color: transparent;
  color: rgb(var(--v-theme-secondary-c900));
  border: 1px solid currentColor;
  opacity: 0.4;
  transition: opacity 0.3s;
  cursor: pointer;

  &:hover,
  &[aria-expanded="true"] {
    opacity: 1;
  }
  .v-theme--dark & {
    color: rgb(var(--v-theme-secondary-c500));
  }

  &--soft {
    color: rgb(var(--v-theme-primary-c700));
    // background-color: transparent;
    .v-theme--dark & {
      // background-color: transparent;
      color: rgb(var(--v-theme-primary-c100));
    }
  }
}

.verse .verse__content {
  position: relative;
  margin-bottom: 0;
  line-height: 2em;

  .verse--heb & {
    text-align: right;
    direction: rtl;
    font-family: "SBL Hebrew", serif !important;
  }

  &--dense {
    line-height: 1.7em;
  }
}
</style>
